import styles from 'components/Ad/styles.scss';
import Splash from 'components/Shared/Splash';
import Brand from 'components/Shared/Brand';
import OnTvBadge from 'components/Shared/OnTvBadge';
import Image from 'components/Shared/Image';
import Button from 'components/Shared/Button';
import { IconArrow, IconCheckmark } from 'components/Shared/Icon';
import cn from 'classnames';
import { Block } from 'components/Shared/Layout/';
import { NotifyLink } from 'components/Shared/NotifyLink';
import { AdTemplate } from 'models/AdTemplate';
import { Ad } from 'models/Ad';
import { SliderAd as SliderAdModel } from 'models/SliderAd';
import {
  MergedProps,
  ImageAdProps,
  ComposedAdProps,
  getCreative,
  getImage,
  ImageAdType,
  ComposedAdType,
  getSlot
} from 'components/Ad';
import { pictureDimensions } from './pictureDimensions';
import { useIntersectionObserver } from 'hooks';
import { TrackedElement } from 'hooks/useIntersectionObserver';
import { GtmPromotion } from 'store/googleTagManager/types';

export const shouldRenderUsps = (appearanceType: ComposedAdType, ad: Ad): ad is SliderAdModel =>
  appearanceType === AdTemplate.PanoramaTwoThirds && (ad as SliderAdModel).usps !== undefined;

export const createImageAd =
  (appearanceType: ImageAdType) =>
  ({ position = 1, ad, className, onAdView, onAdClick }: ImageAdProps & MergedProps) => {
    const image = getImage(ad);
    const dimension = pictureDimensions(appearanceType);
    const { ticket, googleAnalyticsKey, googleAnalyticsName, url, title } = ad;

    const { setElementRef } = useIntersectionObserver({
      onVisible: handleElementVisible
    });

    function handleElementVisible(element: TrackedElement<GtmPromotion>) {
      onAdView(element.payload);
    }

    const gtmPromotion = {
      promotion_id: googleAnalyticsKey,
      promotion_name: googleAnalyticsName,
      creative_name: getCreative(appearanceType),
      creative_slot: getSlot(position)
    };

    const handleOnClick = () => {
      onAdClick(gtmPromotion);
    };

    const adContent = url ? (
      <NotifyLink
        ticket={ticket}
        url={url}
        className={styles.imageAdWrapper}
        onClick={handleOnClick}
      >
        <Image className={styles.adImage} {...image} {...dimension} paddingBottom alt={title} />
      </NotifyLink>
    ) : (
      <div className={styles.imageAdWrapper}>
        <Image className={styles.adImage} {...image} {...dimension} paddingBottom alt={title} />
      </div>
    );

    return (
      <Block
        ref={node => setElementRef({ ref: node, payload: gtmPromotion })}
        className={cn(styles.imageAd, styles.base, className)}
      >
        {adContent}
      </Block>
    );
  };

export const createComposedAd =
  (appearanceType: ComposedAdType) =>
  ({
    ad,
    gotoOfferText,
    className,
    shadow = false,
    position = 1,
    appearance = 'normal',
    onAdView
  }: ComposedAdProps & MergedProps) => {
    const dimension = pictureDimensions(appearanceType);
    const {
      ticket,
      googleAnalyticsKey,
      googleAnalyticsName,
      url,
      title,
      splashTextBottom,
      splashTextEnd,
      splashTextMiddle,
      splashTextTop,
      brandImage,
      isOnTv,
      adImage,
      preTitle,
      description
    } = ad;

    const { setElementRef } = useIntersectionObserver({
      onVisible: handleElementVisible
    });

    function handleElementVisible(element: TrackedElement<GtmPromotion>) {
      onAdView(element.payload);
    }

    const gtmPromotion = {
      promotion_id: googleAnalyticsKey,
      promotion_name: googleAnalyticsName,
      creative_name: getCreative(appearanceType),
      creative_slot: getSlot(position)
    };

    return (
      <Block
        ref={node => setElementRef({ ref: node, payload: gtmPromotion })}
        shadow={(shadow && ((appearanceType === AdTemplate.Medium && 'three') || 'two')) || false}
        className={cn(
          styles.base,
          {
            [styles.mediumAd]: appearanceType === AdTemplate.Medium,
            [styles.panoramaAd]: appearanceType === AdTemplate.PanoramaTwoThirds,
            [styles.slimmed]: appearance === 'slimmed'
          },
          className
        )}
      >
        <NotifyLink ticket={ticket} url={url}>
          <Splash
            splashTextTop={splashTextTop}
            splashTextMiddle={splashTextMiddle}
            splashTextBottom={splashTextBottom}
            splashTextEnd={splashTextEnd}
            appearance={appearance === 'slimmed' ? 'small' : undefined}
            className={styles.splash}
          />
          {brandImage && (
            <div className={styles.brandContainer}>
              <Brand appearance={appearance === 'slimmed' ? 'small' : 'medium'} {...brandImage} />
            </div>
          )}
          <div className={styles.imageWrapper}>
            {isOnTv && <OnTvBadge className={styles.onTvBadge} />}
            <Image
              className={styles.adImage}
              paddingBottom
              {...adImage}
              {...dimension}
              alt={title}
            />
          </div>
          <div className={styles.adInfo}>
            <div className={styles.adInfoUpper}>
              <h5 className={styles.preTitle}>{preTitle}</h5>
              <h4 className={styles.title}>{title}</h4>
            </div>
            <div className={styles.adInfoLower}>
              <p className={styles.description}>{description}</p>
              {shouldRenderUsps(appearanceType, ad) && (
                <ul className={styles.usps}>
                  {ad.usps.map(usp => (
                    <li key={usp}>
                      <IconCheckmark size="small" />
                      {usp}
                    </li>
                  ))}
                </ul>
              )}
              <Button className={styles.adButton}>
                {gotoOfferText}
                <IconArrow size="small" className={styles.adButtonArrow} />
              </Button>
            </div>
          </div>
        </NotifyLink>
      </Block>
    );
  };
